import React, { useEffect, useMemo, useState } from 'react';
import {
  CategoryOrSegmentsLeaderboardGroupContext,
  CategoryOrSegmentLeaderboardGroupType,
} from './CategoryOrSegmentsLeaderboardGroupContext';
import { EditGroupMode, CategoryAndSegmentsLeaderboardGroupDetails } from '@models/CategoryLeaderboard';
import { FeatureOption, PageType } from '@utils/pageConfig/Types';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

const CategoryOrSegmentsLeaderboardGroupProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  const [selectedProductGroupsOrSegments, setSelectedProductGroupsOrSegments] = useState<string[]>([]);
  const [isCreateModalGroupPopUp, setIsCreateModalGroupPopUp] = useState<boolean>(false);
  const [groupNameToBeDelete, setGroupNameToBeDelete] = useState<string>('');
  const [isDeleteModalGroupPopUp, setIsDeleteModalGroupPopUp] = useState<boolean>(false);
  const [currentSelectedFeature, setCurrentSelectedFeature] = useState<FeatureOption>(CATEGORY_FEATURE_OPTION);
  const [isShareModalGroupPopUp, setIsShareModalGroupPopUp] = useState<boolean>(false);

  const pagesConfig = usePageConfig();
  useEffect(() => {
    //We will only update the currentSelectedFeature if current page is explore page.
    //This is because Category Leaderboard doesn't have feature selection
    if (pagesConfig.pageType === PageType.EXPLORE) {
      setCurrentSelectedFeature(mainFiltersState.feature);
    }
  }, [mainFiltersState.feature]);

  const groupDetails: CategoryAndSegmentsLeaderboardGroupDetails = {
    groupName: '',
    segmentOrPgValue: [],
    sortOrder: undefined,
  };

  const initialEditGroupMode: EditGroupMode = {
    groupDetails: groupDetails,
    isEditMode: false,
  };

  const [isEditGroupMode, setIsEditGroupMode] = useState<EditGroupMode>(initialEditGroupMode);

  const selectProductGroupOrSegments = (productGroupOrSegments: string) => {
    if (isProductGroupOrSegmentsSelected(productGroupOrSegments)) {
      setSelectedProductGroupsOrSegments(
        selectedProductGroupsOrSegments.filter((selectedPG) => selectedPG !== productGroupOrSegments),
      );
    } else {
      setSelectedProductGroupsOrSegments([...selectedProductGroupsOrSegments, productGroupOrSegments]);
    }
  };

  const clearSelectedPG = () => setSelectedProductGroupsOrSegments([]);

  const isProductGroupOrSegmentsSelected = (productGroup: string) =>
    selectedProductGroupsOrSegments.includes(productGroup);

  const setIsCreateModalGroupShown = (value: boolean) => {
    setIsCreateModalGroupPopUp(value);
  };

  const setIsDeleteModalGroupShown = (value: boolean) => {
    setIsDeleteModalGroupPopUp(value);
  };

  const setIsShareModalGroupShown = (value: boolean) => {
    setIsShareModalGroupPopUp(value);
  };

  const selectGroupNameToBeDeleted = (groupName: string) => {
    setGroupNameToBeDelete(groupName);
  };

  const resetGroupingState = () => {
    clearSelectedPG();
    setIsCreateModalGroupPopUp(false);
    setGroupNameToBeDelete('');
    setIsDeleteModalGroupPopUp(false);
    setIsEditGroupMode(initialEditGroupMode);
  };

  const contextValue: CategoryOrSegmentLeaderboardGroupType = useMemo(
    () => ({
      selectedProductGroupOrSegments: selectedProductGroupsOrSegments,
      selectProductGroupOrSegments: selectProductGroupOrSegments,
      clearSelectedPgsOrSegments: clearSelectedPG,
      setSelectedPgsOrSegments: setSelectedProductGroupsOrSegments,
      isProductGroupOrSegmentsSelected: isProductGroupOrSegmentsSelected,
      isCreateModalGroupPopUp: isCreateModalGroupPopUp,
      setIsCreateModalGroupPopUp: setIsCreateModalGroupShown,
      groupNameToBeDelete: groupNameToBeDelete,
      setGroupNameToBeDelete: selectGroupNameToBeDeleted,
      isDeleteModalGroupPopUp: isDeleteModalGroupPopUp,
      setIsDeleteModalGroupPopUp: setIsDeleteModalGroupShown,
      isShareModalGroupPopUp: isShareModalGroupPopUp,
      setIsShareModalGroupPopUp: setIsShareModalGroupShown,
      isEditGroupMode: isEditGroupMode,
      setIsEditGroupMode: setIsEditGroupMode,
      resetGroupingState: resetGroupingState,
      currentSelectedFeature: currentSelectedFeature,
      setCurrentSelectedFeature: setCurrentSelectedFeature,
    }),
    [
      selectedProductGroupsOrSegments,
      selectProductGroupOrSegments,
      clearSelectedPG,
      isProductGroupOrSegmentsSelected,
      isCreateModalGroupPopUp,
      setIsCreateModalGroupShown,
      groupNameToBeDelete,
      selectGroupNameToBeDeleted,
      isDeleteModalGroupPopUp,
      setIsDeleteModalGroupShown,
      isShareModalGroupPopUp,
      setIsShareModalGroupShown,
      isEditGroupMode,
      setIsEditGroupMode,
      resetGroupingState,
      currentSelectedFeature,
      setCurrentSelectedFeature,
    ],
  );

  return (
    <CategoryOrSegmentsLeaderboardGroupContext.Provider value={contextValue}>
      {children}
    </CategoryOrSegmentsLeaderboardGroupContext.Provider>
  );
};

export default CategoryOrSegmentsLeaderboardGroupProvider;
